/**
 * StatGrid + StatCard
 *
 * Token-based metric cards used by ReviewImportStep's summary and complete
 * phases. Each card shows an icon, a numeric value, and a label.
 */
import { ReactNode } from 'react'
import { cn } from 'components/UI/cn'

export interface StatItem {
  icon: ReactNode
  value: number
  label: string
  /** Card background token class (e.g. 'bg-surface-sunken'). */
  bg: string
  /** Value text token class (e.g. 'text-fg'). */
  textColor: string
}

interface StatGridProps {
  items: StatItem[]
  className?: string
}

/** Renders a responsive row of metric cards. */
export function StatGrid({ items, className }: StatGridProps) {
  return (
    <div
      className={cn(
        'grid grid-cols-2 sm:grid-cols-3 gap-3 lg:gap-4',
        className
      )}
    >
      {items.map((item) => (
        <StatCard key={item.label} {...item} />
      ))}
    </div>
  )
}

/** A single metric card with icon, value, and label. */
export function StatCard({ icon, value, label, bg, textColor }: StatItem) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-2 px-4 py-5 rounded-xl border border-line',
        bg
      )}
    >
      <div className="flex items-center justify-center">{icon}</div>
      <p
        className={cn(
          'text-2xl font-bold leading-none tabular-nums',
          textColor
        )}
      >
        {value.toLocaleString()}
      </p>
      <p className="text-xs text-fg-secondary text-center">{label}</p>
    </div>
  )
}
